/* eslint-disable */
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Inbox, Clock, CheckCircle2, ArrowRight, Layers,
} from "lucide-react";
import RenderSupportPage from "./RenderSupportPage";
import { GetTicketStats, GetTickets } from "../services/Actions/supportAction";
import { StatusBadge, PriorityBadge, timeAgo } from "./components/TicketBadges";

const TILES = [
  { key: "open", label: "Open", icon: <Inbox size={20} />, color: "sky", view: "open" },
  { key: "pending", label: "Awaiting Member", icon: <Clock size={20} />, color: "violet", view: "pending" },
  { key: "resolved_today", label: "Resolved Today", icon: <CheckCircle2 size={20} />, color: "emerald", view: "resolved" },
];

const colorMap = {
  sky: { bg: "bg-sky-50 dark:bg-sky-900/20", icon: "bg-sky-100 dark:bg-sky-900/40 text-sky-600 dark:text-sky-400", border: "border-sky-100 dark:border-sky-800", arrow: "text-sky-500" },
  amber: { bg: "bg-amber-50 dark:bg-amber-900/20", icon: "bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400", border: "border-amber-100 dark:border-amber-800", arrow: "text-amber-500" },
  rose: { bg: "bg-rose-50 dark:bg-rose-900/20", icon: "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400", border: "border-rose-100 dark:border-rose-800", arrow: "text-rose-500" },
  violet: { bg: "bg-violet-50 dark:bg-violet-900/20", icon: "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400", border: "border-violet-100 dark:border-violet-800", arrow: "text-violet-500" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-900/20", icon: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400", border: "border-emerald-100 dark:border-emerald-800", arrow: "text-emerald-500" },
};

function SupportDashboard() {
  const history = useHistory();
  const dispatch = useDispatch();
  const support = useSelector((state) => state.support);
  const { stats, tickets } = support;

  useEffect(() => {
    dispatch(GetTicketStats());
    // Recent activity strip — newest first, capped client-side.
    dispatch(GetTickets({ limit: 8, sort: "-updated_at" }));
  }, [dispatch]);

  const byQueue = stats?.by_queue || [];
  const recent = (tickets || []).slice(0, 8);

  return (
    <RenderSupportPage title="Support Dashboard">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-6">
        Member tickets across loans, mart, funds and account support.
      </p>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((tile) => {
          const c = colorMap[tile.color];
          return (
            <button
              key={tile.key}
              onClick={() => history.push(`/support/tickets?view=${tile.view}`)}
              className={`text-left p-5 rounded-2xl border ${c.border} ${c.bg} hover:shadow-md transition-all group`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.icon}`}>
                  {tile.icon}
                </div>
                <ArrowRight size={16} className={`${c.arrow} opacity-0 group-hover:opacity-100 transition-opacity`} />
              </div>
              <p className="text-2xl font-black text-slate-800 dark:text-slate-100 leading-none">
                {stats?.[tile.key] ?? 0}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 font-semibold">
                {tile.label}
              </p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Queue breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers size={16} className="text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">By Queue</h3>
          </div>
          {byQueue.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-slate-500 py-6 text-center">
              No queue data yet.
            </p>
          ) : (
            <div className="space-y-3">
              {byQueue.map((row) => {
                const total = byQueue.reduce((s, r) => s + (r.count || 0), 0) || 1;
                const pct = Math.round(((row.count || 0) / total) * 100);
                return (
                  <button
                    key={row.queue}
                    onClick={() => history.push(`/support/tickets?queue=${row.queue}`)}
                    className="w-full text-left group"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                        {String(row.queue || "").replace(/_/g, " ")}
                      </span>
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                        {row.count ?? 0}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Recent Activity</h3>
            <button
              onClick={() => history.push("/support/tickets")}
              className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              View all
            </button>
          </div>

          {recent.length === 0 ? (
            <div className="py-10 text-center">
              <Inbox size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-xs text-slate-400 dark:text-slate-500">
                No tickets to show.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-gray-700">
              {recent.map((t) => (
                <button
                  key={t._id || t.ticket_no}
                  onClick={() => history.push(`/support/ticket-detailed?id=${t._id || t.ticket_no}`)}
                  className="w-full text-left py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-gray-700/40 rounded-lg px-2 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">
                      {t.subject || "Untitled ticket"}
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                      #{t.ticket_no || "—"} · {t.member?.name || t.member_name || "Unknown member"} · {timeAgo(t.updated_at || t.updatedAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <PriorityBadge priority={t.priority} />
                    <StatusBadge status={t.status} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </RenderSupportPage>
  );
}

export default SupportDashboard;
